import { z } from 'zod';
import { CustomSandboxSizeSchema, ResourceSizeSchema } from '@runloop/reflex-contract';

export const BlueprintStatusSchema = z.enum([
  'queued',
  'provisioning',
  'building',
  'failed',
  'build_complete',
]);

export type BlueprintStatus = z.infer<typeof BlueprintStatusSchema>;

/**
 * Raw Runloop build statuses that reflex folds into one of its own, rather
 * than modeling separately.
 *
 * `awaiting_upload` (added by `@runloop/api-client` 1.29.0) is the step
 * between `building` and `build_complete` where the built image is being
 * pushed. Reflex has no user-actionable distinction there — the UI's only
 * decisions are "is it launchable" (`build_complete`) and "did it fail" — so
 * giving it its own badge, colour, and filter group would be a product change
 * smuggled in by a dependency bump.
 */
const BLUEPRINT_STATUS_ALIASES: Readonly<Record<string, BlueprintStatus>> = {
  awaiting_upload: 'building',
};

/**
 * Coerce a raw Runloop blueprint status into our {@link BlueprintStatus}.
 *
 * An unrecognized status means the platform has a build state this reflex
 * build has never heard of. That resolves to `building`, which is the only
 * non-destructive answer: `failed` would hide a working build behind an error,
 * and `build_complete` is the gate everything uses to decide an image is
 * launchable, so guessing it would offer users a half-built devbox. Reporting
 * "still working" fails closed and self-corrects on the next poll.
 */
export function coerceBlueprintStatus(raw: string | null | undefined): BlueprintStatus {
  const parsed = BlueprintStatusSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  return (typeof raw === 'string' ? BLUEPRINT_STATUS_ALIASES[raw] : undefined) ?? 'building';
}

/**
 * CPU architecture a blueprint image is built for. Matches Runloop's
 * `launch_parameters.architecture` values; `x86_64` is the implicit default
 * everywhere an architecture is absent.
 */
export const BlueprintArchitectureSchema = z.enum(['x86_64', 'arm64']);

export type BlueprintArchitecture = z.infer<typeof BlueprintArchitectureSchema>;

/**
 * Coerce a raw devbox architecture string (Runloop's
 * `launch_parameters.architecture`) into our {@link BlueprintArchitecture}
 * enum. Any unknown, empty, or null value maps to `null` — callers treat that
 * as "architecture unknown", not as the x86_64 default, since a box launched
 * without an explicit architecture never reports one.
 */
export function coerceBlueprintArchitecture(
  raw: string | null | undefined,
): BlueprintArchitecture | null {
  const parsed = BlueprintArchitectureSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * One row of the blueprint create form's "Build Secrets" editor.
 *
 * - `envVar` is the environment variable name the build step sees (e.g.
 *   `NPM_TOKEN`). Same shape constraint as a conventional env var so the
 *   name is safe to inject straight into Runloop's `secrets` map.
 * - `secretRef` references a stored org-tier secret by friendly name (e.g.
 *   `NPM_AUTH_TOKEN`). Scoped (`sec_*`) refs are not accepted server-side;
 *   blueprints are an org-scoped resource and only the org tier makes sense
 *   as the "matching context" for a build.
 *
 * The server resolves each row to Runloop's
 * `{envVar: <runloop_secret_name>}` shape via `SecretService.getDevboxSecrets`
 * before forwarding to `blueprints.create({ secrets })`. Values are never
 * baked into the resulting image; Runloop scopes them to the build phase only.
 */
export const BlueprintBuildSecretRefSchema = z.object({
  envVar: z
    .string()
    .min(1)
    .regex(
      /^[A-Z][A-Z0-9_]*$/,
      'envVar must start with an uppercase letter and contain only uppercase letters, digits, and underscores',
    ),
  secretRef: z.string().min(1),
});
export type BlueprintBuildSecretRef = z.infer<typeof BlueprintBuildSecretRefSchema>;

export const BlueprintSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status: BlueprintStatusSchema,
    createTimeMs: z.number(),
    buildFinishTimeMs: z.number().optional(),
    metadata: z.record(z.string(), z.string()).optional(),
    failureReason: z.string().optional(),
    dockerfile: z.string().optional(),
    resourceSize: ResourceSizeSchema.optional(),
    /**
     * Custom devbox dimensions the blueprint was built with, decoded from
     * Runloop's `CUSTOM_SIZE` launch parameters. Present only when the
     * blueprint pins a custom size — `resourceSize` is absent in that case,
     * since Runloop reports `CUSTOM_SIZE`, which has no named tier. Surfaced
     * so the Clone form can re-offer the same dimensions.
     */
    customSize: CustomSandboxSizeSchema.optional(),
    systemSetupCommands: z.array(z.string()).optional(),
    /**
     * Shell commands Runloop runs on every devbox boot before the agent process
     * starts (mapped to `launch_parameters.launch_commands`). Distinct from
     * `systemSetupCommands`, which are baked into the image at build time.
     */
    launchCommands: z.array(z.string()).optional(),
    baseBlueprintName: z.string().optional(),
    fileMounts: z.record(z.string(), z.string()).optional(),
    installCommand: z.string().optional(),
    /** Git branch or tag baked into the repository code mount. */
    repoBranch: z.string().optional(),
    /**
     * Build-time `--build-arg` values baked into a from-scratch (Dockerfile)
     * build. Surfaced read-side so Edit prefills them and Rebuild replays
     * them; plain `{key: value}`, no resolution needed.
     */
    buildArgs: z.record(z.string(), z.string()).optional(),
    /**
     * Org-tier build secrets resolved back to `{envVar, secretRef}` rows so
     * the Edit form can repopulate and preserve them. Runloop stores these on
     * the blueprint as opaque secret names; the server reverse-maps each to
     * its friendly org-secret ref before returning. Entries whose underlying
     * secret was deleted are omitted (they can't be replayed).
     */
    buildSecrets: z.array(BlueprintBuildSecretRefSchema).optional(),
    /**
     * CPU architecture the image was built for, read from Runloop's
     * `launch_parameters.architecture`. Absent on the wire means `x86_64`;
     * the server always stamps the resolved value.
     */
    architecture: BlueprintArchitectureSchema.optional(),
  })
  .meta({ id: 'Blueprint' });

export type Blueprint = z.infer<typeof BlueprintSchema>;

/**
 * Name of the Runloop-owned, cross-tenant public base blueprint that every
 * org's devboxes layer on top of. It lives outside any single tenant and is
 * referenced by name (`blueprint_name`) so the same published image resolves
 * for every org without an id lookup. Must match the published Runloop
 * blueprint exactly — changing it breaks `listPublic` discovery and the
 * derived-base `base_blueprint_name` parentage.
 */
export const PUBLIC_BASE_BLUEPRINT_NAME = 'runloop/reflex_base_blueprint';

/**
 * Name of the per-org derived base blueprint the bootstrap service maintains.
 * Every org's derived base is created under this exact name in the org's own
 * Runloop tenant; child blueprints reference it via `base_blueprint_name`.
 */
export const BASE_BLUEPRINT_NAME = 'base';

/**
 * Whether `name` refers to one of the standard bases the bootstrap service
 * manages (the per-org derived `base` or the shared public base) rather than a
 * user-created custom base blueprint. Custom bases carry their own
 * architecture, so callers use this to decide when an architecture check
 * against the org's base setting is needed.
 */
export function isStandardBaseBlueprintName(name: string): boolean {
  return name === BASE_BLUEPRINT_NAME || name === PUBLIC_BASE_BLUEPRINT_NAME;
}

/**
 * Resolves the base-blueprint name a create/rebuild should send to Runloop.
 * Runloop resolves `base_blueprint_name` only among *successfully built*
 * blueprints in the org's own tenant, but a vanilla org never builds a
 * per-org `base` — it shares the public base (which IS resolvable by name
 * cross-tenant; the bootstrap service parents org bases on it the same way).
 * So a requested `base` maps to the public base name unless the org has a
 * completed `base` build of its own. Any other name (custom bases, the
 * public name itself) passes through untouched.
 */
export function resolveBaseBlueprintNameForCreate(
  requestedName: string,
  blueprints: ReadonlyArray<{ name: string; status?: string | null }>,
): string {
  if (requestedName !== BASE_BLUEPRINT_NAME) return requestedName;
  const hasCompletedOrgBase = blueprints.some(
    (bp) => bp.name === BASE_BLUEPRINT_NAME && bp.status === 'build_complete',
  );
  return hasCompletedOrgBase ? BASE_BLUEPRINT_NAME : PUBLIC_BASE_BLUEPRINT_NAME;
}

/**
 * Display name for a blueprint. The shared public base carries a long,
 * tenant-prefixed Runloop name (`runloop/reflex_base_blueprint`) that means
 * nothing to a user; everywhere it's shown to a human it reads as `base`, the
 * same label org-derived bases already use. This is a presentation-only
 * mapping — the stored `name` (and anything sent to Runloop) stays the real
 * name so launches resolve the published image by name.
 */
export function displayBlueprintName(name: string): string {
  return name === PUBLIC_BASE_BLUEPRINT_NAME ? 'base' : name;
}

// Mirrors `RepoAccessSchema` in agent.types.ts. Kept as a local definition
// rather than a shared import to avoid an agent → sandbox → blueprint → agent
// module cycle (the shared barrel evaluates these in dependency order).
export const BlueprintRepoAccessSchema = z.enum(['connected', 'public']);
export type BlueprintRepoAccess = z.infer<typeof BlueprintRepoAccessSchema>;

export const CreateBlueprintRequestSchema = z.object({
  name: z.string().min(1),
  dockerfile: z.string(),
  userMode: z.enum(['root', 'user']),
  resourceSize: ResourceSizeSchema.default('X_LARGE'),
  /**
   * Org-defined custom devbox size to build the blueprint at. When set it
   * takes precedence over `resourceSize`: the server maps it onto Runloop's
   * `resource_size_request: 'CUSTOM_SIZE'` + `custom_cpu_cores` /
   * `custom_gb_memory` / `custom_disk_size`, mirroring the agent launch path.
   * Omitted/null means use the named `resourceSize` tier.
   */
  customSize: CustomSandboxSizeSchema.nullable().optional(),
  /** GitHub repository in 'owner/repo' format (e.g., 'runloop/reflex') */
  repoSlug: z.string().optional(),
  /** How the repository should be mounted. Defaults to connected GitHub App access. */
  repoAccess: BlueprintRepoAccessSchema.optional(),
  /** Git branch or tag to check out. Omit to use the repository's default branch. */
  repoBranch: z.string().optional(),
  installCommand: z.string().optional(),
  systemSetupCommands: z.array(z.string()).optional(),
  launchCommands: z.array(z.string()).optional(),
  baseBlueprintName: z.string().optional(),
  fileMounts: z.record(z.string(), z.string()).optional(),
  buildArgs: z.record(z.string(), z.string()).optional(),
  /**
   * Org-tier secrets to inject as environment variables during the build.
   * Each entry binds an env var name to a stored org secret by friendly
   * name; the server resolves to Runloop secret names before calling
   * `blueprints.create`. Empty / omitted means no build-time secrets.
   */
  buildSecrets: z.array(BlueprintBuildSecretRefSchema).optional(),
  /**
   * Mark the created blueprint as a base image (stamps `metadata.type='base'`
   * server-side so `isBaseBlueprint` recognises it). Set when the form is
   * cloning an existing base so the derivative stays in the Base section.
   */
  isBase: z.boolean().optional(),
  /**
   * CPU architecture for a from-scratch (Dockerfile) build, forwarded to
   * Runloop's `launch_parameters.architecture`. Only valid without
   * `baseBlueprintName`: a derived blueprint inherits its parent's
   * architecture (Runloop stamps the resolved value), so the server rejects
   * the combination with 400. Omitted means `x86_64`.
   */
  architecture: BlueprintArchitectureSchema.optional(),
});

export type CreateBlueprintRequest = z.infer<typeof CreateBlueprintRequestSchema>;

/**
 * Per-org "base image" view shown in Admin → Base Image. The base image
 * is NOT a normal blueprint — it's a layered config that the bootstrap
 * service maintains: core commands from the host, plus plugin-contributed
 * commands, plus per-org overrides. Only the overrides are editable; the
 * other two sections are surfaced read-only so operators can see exactly
 * what will land in the image.
 */
export const BaseImagePluginContributionSchema = z.object({
  pluginName: z.string(),
  commands: z.array(z.string()),
  /** Short human-readable description of what this plugin's commands install/configure. */
  description: z.string().optional(),
});

export type BaseImagePluginContribution = z.infer<typeof BaseImagePluginContributionSchema>;

/**
 * One annotated shell command in the host-provided core layer of the base
 * image. `label` is a short one-liner (e.g. "Claude Code") used for the
 * at-a-glance summary, and `description` is the fuller explanation shown
 * when a row is expanded. Both are metadata for the Base Blueprint UI and
 * are not passed to the sandbox provider — only `command` lands in the
 * `system_setup_commands` parameter on the actual Runloop blueprint.
 */
export const BaseImageCoreCommandSchema = z.object({
  command: z.string(),
  description: z.string(),
  /** Short one-liner title for the at-a-glance summary view. */
  label: z.string().optional(),
});

export type BaseImageCoreCommand = z.infer<typeof BaseImageCoreCommandSchema>;

export const BaseImageViewSchema = z
  .object({
    /** Resource size the base blueprint is built at (currently `MEDIUM`). */
    resourceSize: ResourceSizeSchema,
    /** Dockerfile (unrendered) that frames the base build. Read-only. */
    dockerfile: z.string(),
    /**
     * Standalone, runnable Dockerfile rendering of the whole base image:
     * the framing dockerfile plus every merged setup command as a RUN line
     * and the file mounts as COPY heredocs. Buildable as-is with
     * `docker build - < Dockerfile`. Read-only.
     */
    dockerfileExample: z.string(),
    /** Commands the host always bakes in (annotated). Read-only. */
    coreSetupCommands: z.array(BaseImageCoreCommandSchema),
    /** Commands contributed by every installed plugin, grouped per plugin. */
    pluginContributions: z.array(BaseImagePluginContributionSchema),
    /** Editable per-org extras (free-form shell commands). */
    orgOverrideCommands: z.array(z.string()),
    /** The fully merged list, in the exact order baked into the image. */
    mergedSetupCommands: z.array(z.string()),
    /** Build status of the most recent base blueprint, if any. */
    currentStatus: BlueprintStatusSchema.optional(),
    /** `true` iff the merged config differs from the latest completed build. */
    rebuildPending: z.boolean(),
    /**
     * CPU architecture the org base is configured for. `arm64` builds the org
     * base standalone (no public-base parent) and cascades to every derived
     * blueprint and devbox; `x86_64` is today's layered default.
     */
    architecture: BlueprintArchitectureSchema,
  })
  .meta({ id: 'BaseImageView' });

export type BaseImageView = z.infer<typeof BaseImageViewSchema>;

export const UpdateBaseImageOverridesRequestSchema = z.object({
  orgOverrideCommands: z.array(z.string()),
  /**
   * Org base architecture to store alongside the overrides. Omitted means
   * "keep the currently stored value", so existing clients that only edit
   * commands never flip an org's architecture.
   */
  architecture: BlueprintArchitectureSchema.optional(),
});

export type UpdateBaseImageOverridesRequest = z.infer<typeof UpdateBaseImageOverridesRequestSchema>;

export const BlueprintLogEntrySchema = z.object({
  level: z.string(),
  message: z.string(),
  timestampMs: z.number(),
});

export type BlueprintLogEntry = z.infer<typeof BlueprintLogEntrySchema>;

/**
 * Render build log entries as the plain text a reader consumes: one line per
 * entry, with a severity prefix only where the message does not already
 * announce its own (Docker and npm both write their own `ERROR:` lines, and
 * `[ERROR] ERROR: …` reads as noise).
 *
 * Shared because two surfaces render the same log — the Environments UI's
 * build-log panel and the `get_blueprint_build_log` MCP tool — and a caller
 * comparing what an agent quoted against what the UI shows must see the same
 * text.
 */
export function formatBlueprintBuildLog(entries: BlueprintLogEntry[]): string {
  return entries
    .map((entry) => {
      const lower = entry.message.toLowerCase();
      if (entry.level === 'error' && !lower.includes('error') && !lower.includes('fatal')) {
        return `[ERROR] ${entry.message}`;
      }
      if (entry.level === 'warn' && !lower.includes('warn')) {
        return `[WARN] ${entry.message}`;
      }
      return entry.message;
    })
    .join('\n');
}

export const BlueprintLogsResponseSchema = z.object({
  logs: z.array(BlueprintLogEntrySchema),
});

export type BlueprintLogsResponse = z.infer<typeof BlueprintLogsResponseSchema>;

/** Entries per page when a build-log request does not ask for a size. */
export const BLUEPRINT_BUILD_LOG_DEFAULT_LIMIT = 200;
/**
 * Hard ceiling on a page. A full base-image build runs to thousands of lines,
 * and the whole log is one upstream read, so the cap is about what a client
 * has to hold and render, not about what the server can fetch.
 */
export const BLUEPRINT_BUILD_LOG_MAX_LIMIT = 1_000;

/**
 * Page direction. `asc` reads the build from the top, `desc` from the end —
 * which is where a failure is, so a caller diagnosing one wants the first
 * `desc` page and usually nothing more.
 */
export const BlueprintBuildLogOrderSchema = z.enum(['asc', 'desc']);
export type BlueprintBuildLogOrder = z.infer<typeof BlueprintBuildLogOrderSchema>;

export const BlueprintBuildLogQuerySchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(BLUEPRINT_BUILD_LOG_MAX_LIMIT)
    .optional()
    .describe(`Entries per page (default ${BLUEPRINT_BUILD_LOG_DEFAULT_LIMIT}).`),
  /** Opaque; echo back a previous response's `nextCursor` verbatim. */
  cursor: z.string().optional(),
  order: BlueprintBuildLogOrderSchema.optional().describe('Page direction (default asc).'),
});
export type BlueprintBuildLogQuery = z.infer<typeof BlueprintBuildLogQuerySchema>;

/**
 * One page of a blueprint's build log, with the build state that explains it:
 * output alone rarely says whether a build is still running or why it stopped.
 *
 * `totalEntries` counts the whole build, not the page. `nextCursor` is `null`
 * on the last page.
 */
export const BlueprintBuildLogResponseSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: BlueprintStatusSchema,
  /** The build's failure summary, when it failed. */
  failureReason: z.string().nullable(),
  entries: z.array(BlueprintLogEntrySchema),
  totalEntries: z.number().int(),
  nextCursor: z.string().nullable(),
});

export type BlueprintBuildLogResponse = z.infer<typeof BlueprintBuildLogResponseSchema>;
